import React, { useState, useEffect } from 'react';
import './FlipCounter.css';

const FlipDigit = ({ digit }) => {
  const [bottomDigit, setBottomDigit] = useState(digit);
  const [isFlipping, setIsFlipping] = useState(false);
  const [oldDigit, setOldDigit] = useState(digit);

  useEffect(() => {
    if (digit !== bottomDigit) {
      // Start flip animation
      setOldDigit(bottomDigit);
      setIsFlipping(true);

      // After 0.3s, update bottom and end animation
      const timer = setTimeout(() => {
        setBottomDigit(digit);
        setIsFlipping(false);
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [digit, bottomDigit]);

  return (
    <div className="flip-digit-container">
      <div className={`flip-digit ${isFlipping ? 'flipping' : ''}`}>
        {/* Static top - shows new digit */}
        <div className="digit-top">
          <span>{digit}</span>
        </div>

        {/* Static bottom - shows old digit until animation completes */}
        <div className={`digit-bottom ${isFlipping ? 'fading' : ''}`}>
          <span>{bottomDigit}</span>
        </div>

        {/* Animation elements */}
        {isFlipping && (
          <>
            {/* Top flap - shows old digit, flips away */}
            <div className="digit-top-flip">
              <span>{oldDigit}</span>
            </div>

            {/* Bottom flap - shows new digit, flips into view */}
            <div className="digit-bottom-flip">
              <span>{digit}</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const FlipCounter = () => {
  const [count, setCount] = useState(0);
  const [isWeekend, setIsWeekend] = useState(false);

  useEffect(() => {
    const day = new Date().getDay();
    // 0 = Sunday, 6 = Saturday
    setIsWeekend(day === 0 || day === 6);
  }, []);

  useEffect(() => {
    if (isWeekend) return;

    const getTimePhase = () => {
      const now = new Date();
      const hours = now.getHours();
      const minutes = now.getMinutes();
      const currentMinutes = hours * 60 + minutes;

      // 5:30 AM = 330 minutes
      const morningStart = 5 * 60 + 30;
      // 11 PM = 1380 minutes
      const eveningSlowdown = 23 * 60;

      if (currentMinutes >= eveningSlowdown || currentMinutes < morningStart) {
        return 'slow'; // 11pm to 5:30am - slow counting
      }
      return 'fast'; // 5:30am to 11pm - fast counting
    };

    const calculateTargetDocuments = () => {
      const now = new Date();
      const hours = now.getHours();
      const minutes = now.getMinutes();
      const seconds = now.getSeconds();

      // Convert current time to seconds since midnight
      const currentSeconds = hours * 3600 + minutes * 60 + seconds;

      // 5:30 AM in seconds
      const startSeconds = 5.5 * 3600;

      // 11 PM in seconds
      const endSeconds = 23 * 3600;

      // Before 5:30 AM - slow night counting (0 to ~5000)
      if (currentSeconds < startSeconds) {
        // From midnight to 5:30am, slowly count up to about 5000
        const nightProgress = currentSeconds / startSeconds;
        return Math.floor(nightProgress * 5000);
      }

      // After 11 PM - slow counting from 270000 towards end of day
      if (currentSeconds >= endSeconds) {
        // From 11pm to midnight, slowly add up to 5000 more
        const lateNightSeconds = currentSeconds - endSeconds;
        const lateNightTotal = 3600; // 1 hour until midnight
        const lateNightProgress = lateNightSeconds / lateNightTotal;
        return 270000 + Math.floor(lateNightProgress * 5000);
      }

      // During main workday (5:30am to 11pm)
      const workdaySeconds = endSeconds - startSeconds; // 17.5 hours
      const secondsSinceStart = currentSeconds - startSeconds;
      // Start from 5000 (night count) and go to 270000
      const documents = 5000 + Math.floor((secondsSinceStart / workdaySeconds) * 265000);

      return Math.min(Math.max(documents, 0), 275000);
    };

    // Initialize to current target
    const initialTarget = calculateTargetDocuments();
    setCount(initialTarget);

    let timeoutId;
    let lastMidnightCheck = new Date().getDate();

    const scheduleNextUpdate = () => {
      // Check for midnight reset
      const currentDate = new Date().getDate();
      if (currentDate !== lastMidnightCheck) {
        lastMidnightCheck = currentDate;
        setCount(0); // Reset at midnight
      }

      const target = calculateTargetDocuments();
      const phase = getTimePhase();

      setCount(prevCount => {
        // Don't exceed target
        if (prevCount >= target) {
          return prevCount;
        }

        // Add documents based on phase
        const gap = target - prevCount;
        if (phase === 'slow') {
          // Slow phase: add 1 document at a time
          return prevCount + 1;
        } else {
          // Fast phase: add 1-5 documents randomly
          const maxToAdd = Math.min(5, gap);
          const toAdd = Math.floor(Math.random() * maxToAdd) + 1;
          return prevCount + toAdd;
        }
      });

      // Interval based on phase
      let nextInterval;
      if (phase === 'slow') {
        // Slow phase: 3-8 seconds between updates
        nextInterval = 3000 + Math.random() * 5000;
      } else {
        // Fast phase: 0.5-2 seconds between updates
        nextInterval = 500 + Math.random() * 1500;
      }

      timeoutId = setTimeout(scheduleNextUpdate, nextInterval);
    };

    // Start after initial random delay
    const initialDelay = 500 + Math.random() * 1500;
    timeoutId = setTimeout(scheduleNextUpdate, initialDelay);

    return () => clearTimeout(timeoutId);
  }, [isWeekend]);

  // Don't render on weekends
  if (isWeekend) {
    return null;
  }

  const countStr = String(count).padStart(6, '0');
  const digits = countStr.split('');

  return (
    <div className="flip-counter-wrapper">
      <div className="flip-counter">
        <div className="flip-digit-group">
          <FlipDigit digit={digits[0]} />
          <FlipDigit digit={digits[1]} />
          <FlipDigit digit={digits[2]} />
        </div>
        <span className="flip-comma">,</span>
        <div className="flip-digit-group">
          <FlipDigit digit={digits[3]} />
          <FlipDigit digit={digits[4]} />
          <FlipDigit digit={digits[5]} />
        </div>
      </div>
      <p className="flip-counter-label">documents created today using Templafy</p>
    </div>
  );
};

export default FlipCounter;
